/**
 * WorkshopPicker — the shared "Choose a workshop" full-screen picker.
 *
 * ONE picker, two callers. It owns the dialog chrome, the highlighted
 * selection, and the day drawer; it owns NO data. The workshop list, the filter
 * row and the header blurb all arrive as props, so `CreateBookingWorkshopPicker`
 * can feed it the workshops endpoint while `RebookWorkshopPicker` feeds it a
 * booking's rebook targets, without a second component existing (BON-146 —
 * "extract the data source, not the component").
 *
 * Everything below speaks {@link PickerWorkshop}, never an API type. The two
 * envelopes differ; they are normalized by `pickerWorkshop.ts` at the boundary.
 *
 * Behaviour worth knowing: a day cell's "+N more" opens a day drawer listing
 * EVERY workshop that day, and picking there is a DIRECT pick — it closes
 * straight back — unlike the calendar's select-then-confirm footer flow.
 */

import { PickerFooter } from './PickerFooter';
import { PickerHeader } from './PickerHeader';
import type { PickerViewMode } from './pickerViewMode';
import type { PickerWorkshop } from './pickerWorkshop';
import { usePickerDialog } from './usePickerDialog';
import { usePickerSelection } from './usePickerSelection';
import { WorkshopDayDrawer } from './WorkshopDayDrawer';
import { WorkshopPickerBody } from './WorkshopPickerBody';

export interface WorkshopPickerProps {
  /** Already filtered by whoever owns the data + filter state. */
  workshops: PickerWorkshop[];
  loading: boolean;
  error: boolean;
  /** The fixed filter row under the header. Each caller composes its own —
   *  create-a-booking gets a region dropdown, rebooking gets a fixed label. */
  filters: React.ReactNode;
  /** One line under the heading, describing the breadth of the choice. */
  headerSubtitle: React.ReactNode;
  /** Optional strip above the body — a truncated-listing warning, say. */
  notice?: React.ReactNode;
  viewMode: PickerViewMode;
  month: Date;
  onMonthChange: (month: Date) => void;
  /** Subline for the day drawer, e.g. "all regions" or a region name. */
  dayScopeLabel: string;
  emptyMessage: string;
  errorMessage: string;
  /** Return to whatever opened the picker without picking. */
  onBack: () => void;
  /** Close the ENTIRE flow (header ×). */
  onClose: () => void;
  onPick: (workshop: PickerWorkshop) => void;
}

export function WorkshopPicker({
  workshops,
  loading,
  error,
  filters,
  headerSubtitle,
  notice,
  viewMode,
  month,
  onMonthChange,
  dayScopeLabel,
  emptyMessage,
  errorMessage,
  onBack,
  onClose,
  onPick,
}: WorkshopPickerProps) {
  const { titleId, panelRef } = usePickerDialog();
  const s = usePickerSelection(workshops, onBack, onPick);

  return (
    <div
      role="presentation"
      onClick={onBack}
      className="fixed inset-0 z-50 bg-black/50 p-0 sm:p-6 lg:p-10"
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
        className="relative flex h-full w-full flex-col overflow-hidden bg-white shadow-2xl dark:bg-[#1a1f1c] sm:rounded-xl"
      >
        <PickerHeader
          onBack={onBack}
          onClose={onClose}
          titleId={titleId}
          subtitle={headerSubtitle}
        />
        {filters}
        {notice}
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-5 py-4">
          <WorkshopPickerBody
            viewMode={viewMode}
            loading={loading}
            error={error}
            workshops={workshops}
            selectedId={s.selectedId}
            onSelect={s.setSelectedId}
            onDayOverflowClick={s.openDayDrawer}
            month={month}
            onMonthChange={onMonthChange}
            emptyMessage={emptyMessage}
            errorMessage={errorMessage}
          />
        </div>
        <PickerFooter
          disabled={!s.selected}
          onBack={onBack}
          onConfirm={() => s.selected && onPick(s.selected)}
        />
        {s.dayDrawer && (
          <WorkshopDayDrawer
            day={s.dayDrawer}
            workshops={s.drawerWorkshops}
            regionScopeLabel={dayScopeLabel}
            selectedId={s.selectedId}
            onSelect={s.pickFromDrawer}
            onClose={s.closeDayDrawer}
          />
        )}
      </div>
    </div>
  );
}
